import { MouseEvent } from "react";

import { createColumnHelper } from "@tanstack/react-table";
import { Chip, Checkbox, Badge } from "@mui/material";
import { capitalize, find, sortBy, uniq } from "lodash";
import { parseISO, format } from "date-fns";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";

import { type SelectionAction, type TableColumn, type TableRow } from "./types";
import RaggedTextSkeleton from "./RaggedTextSkeleton";
import SelectColumn, {
  ColumnHeader as SelectColumnHeader,
} from "./SelectColumn";

import styles from "./Table.module.css";
import FakeEllipsisOverflow from "./FakeEllipsisOverflow";
import { useGlobalTableState } from "./GlobalTableState";
import {
  APIMonitorResolvedAnomalyAlert,
  APIMonitorUnresolvedAnomalyAlert,
} from "../../../types/APIMonitoringEnrollment";

export const DEFAULT_SELECTION_ACTIONS: SelectionAction[] = [
  "assign",
  "export",
  "pause",
  "resume",
];

export const columnHelper = createColumnHelper<TableRow>();

export const columnDefinitions: Record<
  TableColumn | "_select",
  { whenLoading: any; otherwise: any }
> = {
  _select: {
    whenLoading: {
      id: "select",
      header: () => <SelectColumnHeader disabled />,
      cell: () => <Checkbox disabled />,
    },
    otherwise: new SelectColumn<TableRow>(),
  },
  alerts: {
    whenLoading: {
      id: "alerts",
      header: "Alerts",
      cell: () => <RaggedTextSkeleton />,
      meta: {
        resizable: true,
        relativeSize: 2,
      },
    },
    otherwise: columnHelper.accessor("alerts", {
      id: "alerts",
      meta: {
        filterVariant: "valuePicker",
        sortVariant: "simple",
        resizable: true,
        relativeSize: 2,
      },
      cell: ({ cell, row }) => {
        // TODO(drew): DELETE ME POST TKHOME DEMO
        const domain: string = row.getValue("domain");

        const unresolvedAlerts: APIMonitorUnresolvedAnomalyAlert[] =
          row.getValue("_unresolved_alerts") || [];
        const resolvedAlerts: APIMonitorResolvedAnomalyAlert[] =
          row.getValue("_resolved_alerts") || [];

        return (
          <FakeEllipsisOverflow>
            {uniq(
              domain === "tkhome.ca" ? ["adverse-media-legal"] : cell.getValue()
            ).map((alertType) => {
              const isResolved = resolvedAlerts
                .map((alert) => alert.type)
                .includes(alertType);
              const isUnresolved = unresolvedAlerts
                .map((alert) => alert.type)
                .includes(alertType);
              const isOnlyResolved = isResolved && !isUnresolved;

              const resolvedTitle = "This alert has been resolved";

              let issueId: string | undefined;
              if (isResolved) {
                issueId = find(resolvedAlerts, (resolvedAlert) => {
                  return resolvedAlert.type === alertType;
                })?.anomaly_id;
              } else if (isUnresolved) {
                issueId = find(unresolvedAlerts, (unresolvedAlert) => {
                  return unresolvedAlert.type === alertType;
                })?.anomaly_id;
              }

              const [globalTableState] = useGlobalTableState();

              return (
                <Badge
                  badgeContent={
                    isOnlyResolved ? (
                      <CheckOutlinedIcon
                        style={{ fontSize: "0.8em", color: "#757575" }}
                      />
                    ) : undefined
                  }
                  title={isOnlyResolved ? resolvedTitle : undefined}
                  sx={{
                    "& .MuiBadge-badge": {
                      top: 0,
                      right: 0,
                      transform: "scale(1) translate(0%, 0px)",
                      border: "1px dashed #BDBDBD",
                      padding: 0,
                    },
                  }}
                  key={alertType}
                >
                  <Chip
                    sx={{
                      borderRadius: "5px",
                      marginRight: "0.65em",
                      marginTop: "0.25em",
                      marginBottom: "0.25em",
                      paddingRight: "0.3em",
                      paddingLeft: "0.3em",
                      borderStyle: isOnlyResolved ? "dashed" : "solid",
                      fontWeight: isOnlyResolved ? "normal" : undefined,
                      color: isOnlyResolved ? "#757575" : undefined,
                      borderColor: isOnlyResolved ? "#BDBDBD" : undefined,
                      clipPath: isOnlyResolved
                        ? // knock out a chunk of the border with an inset square so that
                          // it doesn't show through in the background of the badge. Note that
                          // if you want this to correctly work with a non-transparent background color
                          // you'd have to do something fancier to match the shape of the badge here, but that
                          // sounds like a huge PITA.
                          `polygon(
                                  0 0,
                                  calc(100% - 8px) 0,
                                  calc(100% - 8px) 8px,
                                  100% 17px,
                                  100% 100%,
                                  0 100%
                                )`
                        : undefined,
                      cursor: issueId ? "pointer" : undefined,
                    }}
                    size="small"
                    variant="outlined"
                    label={capitalize(alertType)}
                    className={styles.InlineChip}
                    title={isOnlyResolved ? resolvedTitle : undefined}
                    onClick={
                      issueId
                        ? (e: MouseEvent) => {
                            e.preventDefault();
                            e.stopPropagation();

                            if (domain && issueId) {
                              globalTableState?.onOpenMonitorDetail?.(
                                domain,
                                issueId
                              );
                            }
                          }
                        : undefined
                    }
                  />
                </Badge>
              );
            })}
          </FakeEllipsisOverflow>
        );
      },
    }),
  },
  _resolved_alerts: {
    whenLoading: {
      id: "_resolved_alerts",
      cell: () => <RaggedTextSkeleton />,
      meta: {
        private: true,
      },
    },
    otherwise: columnHelper.accessor("_resolved_alerts", {
      id: "_resolved_alerts",
      meta: {
        private: true,
      },
    }),
  },
  _unresolved_alerts: {
    whenLoading: {
      id: "_unresolved_alerts",
      cell: () => <RaggedTextSkeleton />,
      meta: {
        private: true,
      },
    },
    otherwise: columnHelper.accessor("_unresolved_alerts", {
      id: "_unresolved_alerts",
      meta: {
        private: true,
      },
    }),
  },
  assigned_to: {
    whenLoading: {
      id: "assigned_to",
      header: "Assigned To",
      cell: () => <RaggedTextSkeleton />,
      meta: {
        resizable: true,
      },
    },
    otherwise: columnHelper.accessor("assigned_to", {
      id: "assigned_to",
      header: "Assigned To",
      meta: {
        filterVariant: "valuePicker",
        filterStickyValues: ["unassigned"],
        sortVariant: "simple",
        resizable: true,
      },
      cell: ({ cell, row }) => {
        const value = cell.getValue();
        const assignees = value?.map(
          (assignment) => assignment.assigned_to?.email
        );

        if (assignees?.length) {
          return assignees.join(", ");
        }

        return null;
      },
    }),
  },
  last_alerted: {
    whenLoading: {
      id: "last_alerted",
      header: "Last Alert",
      cell: () => <RaggedTextSkeleton />,
      meta: {
        resizable: true,
      },
    },
    otherwise: columnHelper.accessor("last_alerted", {
      id: "last_alerted",
      header: "Last Alert",
      cell: ({ cell }) => {
        const timestamp = cell.getValue();
        if (!timestamp) return "Never";

        try {
          const parsed = parseISO(timestamp);
          return format(parsed, "MMM d, yyyy");
        } catch (e) {
          return timestamp;
        }
      },
      meta: {
        filterVariant: "dateThreshold",
        sortVariant: "simple",
        resizable: true,
      },
    }),
  },
  monitor_types: {
    whenLoading: {
      id: "monitor_types",
      header: "Monitored For",
      cell: () => <RaggedTextSkeleton />,
      meta: {
        resizable: true,
        relativeSize: 2.5,
      },
    },
    otherwise: columnHelper.accessor("monitor_types", {
      id: "monitor_types",
      header: "Monitored For",
      meta: {
        filterVariant: "valuePicker",
        sortVariant: "simple",
        resizable: true,
        relativeSize: 2.5,
      },
      cell: ({ cell, row }) => {
        // TODO(drew): DELETE ME POST TKHOME DEMO
        const domain = row.getValue("domain");

        return (
          <>
            {/* TODO(drew): DELETE ME POST TKHOME DEMO */}
            {sortBy(
              uniq([
                ...cell.getValue(),
                ...(domain === "tkhome.ca" ? ["Adverse media"] : []),
              ])
            )
              .map(capitalize)
              .map((monitorType) => (
                <Chip
                  sx={{
                    borderRadius: "5px",
                    marginRight: "0.25em",
                    marginTop: "0.25em",
                    marginBottom: "0.25em",
                  }}
                  key={monitorType}
                  size="small"
                  variant="outlined"
                  label={monitorType}
                  className={styles.InlineChip}
                />
              ))}
          </>
        );
      },
    }),
  },
  next_run: {
    whenLoading: {
      id: "next_run",
      header: "Next Run",
      cell: () => <RaggedTextSkeleton />,
      meta: {
        resizable: true,
      },
    },
    otherwise: columnHelper.accessor("next_run", {
      id: "next_run",
      header: "Next Run",
      cell: ({ cell }) => {
        const timestamp = cell.getValue();
        if (!timestamp) return "Not scheduled";

        try {
          const parsed = parseISO(timestamp);
          return format(parsed, "MMM d, yyyy");
        } catch (e) {
          return timestamp;
        }
      },
      meta: {
        filterVariant: "dateThreshold",
        sortVariant: "simple",
        resizable: true,
      },
    }),
  },
  domain: {
    whenLoading: {
      id: "domain",
      header: "Domain",
      cell: () => <RaggedTextSkeleton />,
      meta: {
        resizable: true,
        relativeSize: 1.25,
      },
    },
    otherwise: columnHelper.accessor("domain", {
      id: "domain",
      meta: {
        filterVariant: "valuePicker",
        sortVariant: "simple",
        resizable: true,
        relativeSize: 1.25,
      },
    }),
  },
  external_ref_id: {
    whenLoading: {
      id: "external_ref_id",
      header: "External ID",
      cell: () => <RaggedTextSkeleton />,
      meta: {
        resizable: true,
        relativeSize: 1.25,
      },
    },
    otherwise: columnHelper.accessor("external_ref_id", {
      id: "external_ref_id",
      header: "External ID",
      meta: {
        filterVariant: "valuePicker",
        sortVariant: "simple",
        resizable: true,
        relativeSize: 1.25,
      },
    }),
  },
  status: {
    whenLoading: {
      id: "status",
      header: "Status",
      cell: () => <RaggedTextSkeleton />,
      meta: {
        resizable: true,
      },
    },
    otherwise: columnHelper.accessor("status", {
      id: "status",
      meta: {
        filterVariant: "valuePicker",
        sortVariant: "simple",
        resizable: true,
      },
    }),
  },
  id: {
    whenLoading: {
      id: "id",
      header: "Id",
      cell: () => <RaggedTextSkeleton />,
      meta: {
        resizable: true,
      },
    },
    otherwise: columnHelper.accessor("id", {
      id: "id",
      meta: {
        sortVariant: "simple",
        resizable: true,
      },
    }),
  },
};
